import collect from 'collect.js';
import { Modifier, ModifierGroupInterface, ProductInterface } from '../exports/Interfaces';
import { BasketLineItem, BasketLineItemModifier } from '../../store/types';
import { CompiledModifier, CompiledProduct } from '../../providers/MenuProvider';

export default class BasketHelpers {
  public static toggleModifier<T extends BasketLineItem | BasketLineItemModifier>(props: {
    lineItem: T;
    modifierGroup: ModifierGroupInterface;
    entry: Modifier | ProductInterface;
    entryLineItem?: BasketLineItemModifier;
  }): T {
    const {
      modifierGroup,
      entry,
      lineItem,
      entryLineItem = {
        modifier_group_id: modifierGroup.id,
        modifier_id: entry.id,
        quantity: 1,
        modifier_type: entry.type,
        modifiers: [],
      },
    } = props;

    if (modifierGroup.max_count === 1) {
      return {
        ...lineItem,
        modifiers: collect(lineItem.modifiers)
          .reject((item) => item.modifier_group_id === modifierGroup.id)
          .push(entryLineItem)
          .toArray(),
      };
    }

    const isAdded = !!collect(lineItem.modifiers).first(
      (lineItemModifier) =>
        lineItemModifier.modifier_group_id === modifierGroup.id &&
        lineItemModifier.modifier_id === entry.id &&
        lineItemModifier.modifier_type === entry.type,
    );

    if (isAdded || !modifierGroup.max_count) {
      return {
        ...lineItem,
        modifiers: isAdded
          ? collect(lineItem.modifiers)
              .reject(
                (item) =>
                  item.modifier_group_id === modifierGroup.id &&
                  item.modifier_id === entry.id &&
                  item.modifier_type === entry.type,
              )
              .push(entryLineItem)
              .toArray()
          : [...lineItem.modifiers, entryLineItem],
      };
    }

    const selected = collect(lineItem.modifiers).where('modifier_group_id', modifierGroup.id).count();

    if (selected >= modifierGroup.max_count) {
      return;
    }

    return {
      ...lineItem,
      modifiers: [...lineItem.modifiers, entryLineItem],
    };
  }

  public static validateModifiers(
    product: CompiledProduct | CompiledModifier,
    modifiers: BasketLineItemModifier[],
  ): boolean {
    return !this.findInvalidModifierGroup(product, modifiers);
  }

  public static findInvalidModifierGroup(
    product: CompiledProduct | CompiledModifier,
    modifiers: BasketLineItemModifier[],
  ): null | ModifierGroupInterface {
    return collect(product.modifier_groups)
      .filter((item: ModifierGroupInterface) => this.isModifierGroupRequired(item))
      .first((item: ModifierGroupInterface) => collect(modifiers).where('modifier_group_id', item.id).isEmpty());
  }

  public static isModifierGroupRequired(modifierGroup: ModifierGroupInterface): boolean {
    return modifierGroup?.is_required && collect(modifierGroup?.modifiers).where('is_available', true).isNotEmpty();
  }

  static adjustQuantity<T extends BasketLineItem | BasketLineItemModifier>(props: {
    lineItem: T;
    modifierGroup: ModifierGroupInterface;
    entry: Modifier | ProductInterface;
    step: number;
  }): T {
    const { lineItem, entry, modifierGroup, step } = props;
    const lineItemModifiers = [...lineItem.modifiers];
    const entryLineItemIndex = lineItem.modifiers.findIndex(
      (modifier) =>
        modifier.modifier_group_id === modifierGroup.id &&
        modifier.modifier_id === entry.id &&
        modifier.modifier_type === entry.type,
    );

    if (entryLineItemIndex === -1) {
      return;
    }

    lineItemModifiers[entryLineItemIndex].quantity += step;

    if (!lineItemModifiers[entryLineItemIndex].quantity) {
      lineItemModifiers.splice(entryLineItemIndex, 1);
    }

    return { ...lineItem, modifiers: lineItemModifiers };
  }
}
